import React from 'react';
import { useLanguage } from '../context/LanguageProvider';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { motion } from 'framer-motion';
import { Building2, Hotel, Home, UtensilsCrossed, Ship, PartyPopper, HardHat, Mountain, Landmark, Briefcase, Store } from 'lucide-react';
import { FallbackUI } from './FallbackUI';

const iconMap: Record<string, React.ReactNode> = {
  'Real Estate': <Building2 size={28} />,
  'Bienes Raíces': <Building2 size={28} />,
  'Hospitality': <Hotel size={28} />,
  'Hostelería': <Hotel size={28} />,
  'Luxury Villas': <Home size={28} />,
  'Villas de Lujo': <Home size={28} />,
  'Hotels': <Hotel size={28} />,
  'Hoteles': <Hotel size={28} />,
  'Restaurants': <UtensilsCrossed size={28} />,
  'Restaurantes': <UtensilsCrossed size={28} />,
  'Yachts': <Ship size={28} />,
  'Yates': <Ship size={28} />,
  'Private Events': <PartyPopper size={28} />,
  'Eventos Privados': <PartyPopper size={28} />,
  'Construction': <HardHat size={28} />,
  'Construcción': <HardHat size={28} />,
  'Land Investment': <Mountain size={28} />,
  'Inversión en Terrenos': <Mountain size={28} />,
  'Architecture': <Landmark size={28} />,
  'Arquitectura': <Landmark size={28} />,
  'Developers': <Briefcase size={28} />,
  'Promotores': <Briefcase size={28} />,
  'Real Estate Agencies': <Store size={28} />,
  'Agencias Inmobiliarias': <Store size={28} />,
};

export const ExpertiseGrid: React.FC = () => {
  const { t } = useLanguage();
  const { ref, isVisible } = useScrollReveal();

  const categories = t.expertise.categories;

  if (!categories || categories.length === 0) {
    return <FallbackUI message="Expertise categories loading..." />;
  }

  return (
    <section id="expertise" className="relative py-24 md:py-32 bg-vilarias-black">
      <div className="section-padding" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-serif text-3xl md:text-5xl text-white mb-4">
            {t.expertise.headline}
          </h2>
          <div className="h-px w-16 bg-vilarias-accent mx-auto" />
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 max-w-6xl mx-auto">
          {categories.map((cat, index) => (
            <motion.div
              key={cat}
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="group"
            >
              <div className="glass-card rounded-xl p-6 text-center hover:border-vilarias-accent/50 transition-all duration-300 hover:-translate-y-1 cursor-default">
                <div className="text-vilarias-accent mb-3 flex justify-center group-hover:scale-110 transition-transform duration-300">
                  {iconMap[cat] || <Building2 size={28} />}
                </div>
                <span className="text-sm text-vilarias-text-secondary group-hover:text-white transition-colors">
                  {cat}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
